// Review-prompt gate (2026-10-01): one silent prompt-status check per browser
// session, after the app settles. Non-eligible reasons are handled entirely
// server-side (GET never mutates); we only open the popup on eligible.
// 429-aware: if the shared backoff gate is armed at fire time we skip this
// session silently rather than joining the retry storm (2026-09-30 incident).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reviewsAPI } from '../services/reviewsApi';
import { is429Backoff } from '../services/api';

const SESSION_KEY = 'vecindus_review_prompt_checked';
/** Non-intrusive delay after load before we even ask the backend. */
const FIRE_DELAY_MS = 8000;

export function useReviewPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!user || checkedRef.current) return;
    checkedRef.current = true;
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') return;
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* storage unavailable (private mode) — still allow once per mount */
    }
    const timer = window.setTimeout(() => {
      if (is429Backoff()) return; // skip this session; do not join the storm
      reviewsAPI
        .promptStatus()
        .then((data) => {
          if (data?.eligible) {
            // Record shown fire-and-forget; failure must not block the popup.
            reviewsAPI.promptShown().catch(() => {});
            setOpen(true);
          }
          // All non-eligible reasons stay silent by design.
        })
        .catch(() => {
          /* silent: no prompt on transient/auth errors */
        });
    }, FIRE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [user]);

  const close = useCallback(() => setOpen(false), []);
  return { open, close };
}
